package es.cragma.fichajefacil.ui.screens.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import es.cragma.fichajefacil.data.AppRepository
import es.cragma.fichajefacil.model.User
import es.cragma.fichajefacil.model.formatDurationMillis
import es.cragma.fichajefacil.ui.components.RoleBadge
import es.cragma.fichajefacil.ui.components.StyledButtonPrimary
import es.cragma.fichajefacil.ui.components.VerificationBadge
import es.cragma.fichajefacil.ui.i18n.AppLanguage
import es.cragma.fichajefacil.ui.i18n.LocalizedStrings
import es.cragma.fichajefacil.ui.theme.*

@Composable
fun ClockInRecordsScreen(
    currentUser: User,
    repository: AppRepository,
    currentLanguage: AppLanguage = AppLanguage.SPANISH
) {
    val s = remember(currentLanguage) { LocalizedStrings.get(currentLanguage) }
    val companyId = currentUser.companyId ?: ""
    val allRecords = if (currentUser.companyId == null) {
        // Global admin sees all
        repository.getAllCompanies().flatMap { repository.getAllClockInsForCompany(currentUser, it.id) }
    } else {
        repository.getAllClockInsForCompany(currentUser, companyId)
    }

    var selectedFilter by remember { mutableStateOf("ALL") } // ALL, VERIFIED, UNVERIFIED
    var showExportSuccessDialog by remember { mutableStateOf(false) }

    val filteredRecords = remember(allRecords, selectedFilter) {
        when (selectedFilter) {
            "VERIFIED" -> allRecords.filter { it.isLocationVerified }
            "UNVERIFIED" -> allRecords.filter { !it.isLocationVerified }
            else -> allRecords
        }
    }

    val totalCount = allRecords.size
    val verifiedCount = allRecords.count { it.isLocationVerified }
    val unverifiedCount = totalCount - verifiedCount

    Column(
        modifier = Modifier
            .fillMaxSize()
            .background(CragmaBgLight)
            .padding(16.dp)
    ) {
        // Stats Summary
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            Card(
                modifier = Modifier.weight(1f),
                colors = CardDefaults.cardColors(containerColor = CragmaNavyDark),
                shape = RoundedCornerShape(14.dp)
            ) {
                Column(modifier = Modifier.padding(12.dp)) {
                    Text("Total Fichajes", fontSize = 11.sp, color = Color(0xFF94A3B8))
                    Text("$totalCount", fontSize = 22.sp, fontWeight = FontWeight.Bold, color = Color.White)
                }
            }

            Card(
                modifier = Modifier.weight(1f),
                colors = CardDefaults.cardColors(containerColor = Color(0xFFDCFCE7)),
                shape = RoundedCornerShape(14.dp)
            ) {
                Column(modifier = Modifier.padding(12.dp)) {
                    Text("Autenticados", fontSize = 11.sp, color = Color(0xFF15803D))
                    Text("$verifiedCount", fontSize = 22.sp, fontWeight = FontWeight.Bold, color = Color(0xFF166534))
                }
            }

            Card(
                modifier = Modifier.weight(1f),
                colors = CardDefaults.cardColors(containerColor = Color(0xFFFEF3C7)),
                shape = RoundedCornerShape(14.dp)
            ) {
                Column(modifier = Modifier.padding(12.dp)) {
                    Text("No Verificados", fontSize = 11.sp, color = Color(0xFFB45309))
                    Text("$unverifiedCount", fontSize = 22.sp, fontWeight = FontWeight.Bold, color = Color(0xFF92400E))
                }
            }
        }

        Spacer(modifier = Modifier.height(16.dp))

        // Legal Compliance Banner & Official Export
        Card(
            modifier = Modifier.fillMaxWidth(),
            colors = CardDefaults.cardColors(containerColor = Color(0xFFEFF6FF)),
            shape = RoundedCornerShape(14.dp)
        ) {
            Column(modifier = Modifier.padding(14.dp)) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Column(modifier = Modifier.weight(1f)) {
                        Text(
                            text = "⚖️ Cumplimiento Art. 34.9 ET - Registro Oficial de Jornada",
                            fontSize = 13.sp,
                            fontWeight = FontWeight.Bold,
                            color = Color(0xFF1E40AF)
                        )
                        Text(
                            text = "Conservación inmutable de 4 años disponible para la Inspección de Trabajo.",
                            fontSize = 11.sp,
                            color = Color(0xFF1E3A8A)
                        )
                    }

                    Button(
                        onClick = { showExportSuccessDialog = true },
                        colors = ButtonDefaults.buttonColors(containerColor = CragmaNavyDark),
                        shape = RoundedCornerShape(10.dp)
                    ) {
                        Text("📄 Exportar PDF / CSV", fontSize = 11.sp, fontWeight = FontWeight.Bold)
                    }
                }
            }
        }

        Spacer(modifier = Modifier.height(16.dp))

        // Filter Bar
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            FilterChip(
                selected = selectedFilter == "ALL",
                onClick = { selectedFilter = "ALL" },
                label = { Text("Todos ($totalCount)") }
            )
            FilterChip(
                selected = selectedFilter == "VERIFIED",
                onClick = { selectedFilter = "VERIFIED" },
                label = { Text("✓ Autenticados ($verifiedCount)") }
            )
            FilterChip(
                selected = selectedFilter == "UNVERIFIED",
                onClick = { selectedFilter = "UNVERIFIED" },
                label = { Text("⚠ No Verificados ($unverifiedCount)") }
            )
        }

        Spacer(modifier = Modifier.height(12.dp))

        if (filteredRecords.isEmpty()) {
            Card(
                modifier = Modifier.fillMaxWidth(),
                colors = CardDefaults.cardColors(containerColor = Color.White),
                shape = RoundedCornerShape(12.dp)
            ) {
                Text(
                    text = "No se encontraron fichajes para el filtro seleccionado.",
                    fontSize = 13.sp,
                    color = CragmaTextSecondary,
                    modifier = Modifier.padding(20.dp)
                )
            }
        } else {
            LazyColumn(
                verticalArrangement = Arrangement.spacedBy(10.dp),
                modifier = Modifier.fillMaxSize()
            ) {
                items(filteredRecords) { record ->
                    Card(
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(16.dp),
                        colors = CardDefaults.cardColors(containerColor = Color.White),
                        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
                    ) {
                        Column(
                            modifier = Modifier.padding(16.dp)
                        ) {
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.SpaceBetween,
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Column {
                                    Row(verticalAlignment = Alignment.CenterVertically) {
                                        Text(
                                            text = record.userName,
                                            fontSize = 15.sp,
                                            fontWeight = FontWeight.Bold,
                                            color = CragmaNavyDark
                                        )
                                        Spacer(modifier = Modifier.width(6.dp))
                                        RoleBadge(role = record.userRole)
                                    }
                                    Text(
                                        text = if (record.endTimeMillis == null) "🟢 Jornada En Curso" else "🔴 Jornada Finalizada",
                                        fontSize = 12.sp,
                                        fontWeight = FontWeight.SemiBold,
                                        color = if (record.endTimeMillis == null) Color(0xFF15803D) else CragmaTextSecondary
                                    )
                                }

                                VerificationBadge(isVerified = record.isLocationVerified)
                            }

                            Spacer(modifier = Modifier.height(10.dp))
                            HorizontalDivider(color = Color(0xFFF1F5F9))
                            Spacer(modifier = Modifier.height(8.dp))

                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.SpaceBetween
                            ) {
                                Column {
                                    Text("Tiempo Neto Trabajo:", fontSize = 11.sp, color = CragmaTextSecondary)
                                    Text(formatDurationMillis(record.netWorkedMillis), fontSize = 14.sp, fontWeight = FontWeight.Bold, color = CragmaBlueAccent)
                                }

                                Column {
                                    Text("Tiempo en Descanso:", fontSize = 11.sp, color = CragmaTextSecondary)
                                    Text(formatDurationMillis(record.totalBreakMillis), fontSize = 14.sp, fontWeight = FontWeight.Bold, color = CragmaNavyDark)
                                }

                                Column {
                                    Text("Pausas realizadas:", fontSize = 11.sp, color = CragmaTextSecondary)
                                    Text("${record.breaks.size}", fontSize = 14.sp, fontWeight = FontWeight.Bold, color = CragmaNavyDark)
                                }
                            }

                            if (record.startLocation != null) {
                                Spacer(modifier = Modifier.height(8.dp))
                                Text(
                                    text = "📍 Ubicación inicio: ${record.startLocation}",
                                    fontSize = 11.sp,
                                    color = Color(0xFF1E3A8A)
                                )
                            }
                        }
                    }
                }
            }
        }
    }

    if (showExportSuccessDialog) {
        AlertDialog(
            onDismissRequest = { showExportSuccessDialog = false },
            title = {
                Text("📄 Informe Oficial de Registro Generado", fontWeight = FontWeight.Bold, fontSize = 16.sp)
            },
            text = {
                Column {
                    Text(
                        text = "Se ha generado el informe oficial consolidado del registro de jornada laboral según la normativa del Ministerio de Trabajo (Art. 34.9 ET).\n\n• Período de conservación: 4 años inmutables\n• Desglose: Horas ordinarias, pausas y horas extras\n• Formatos exportados: PDF firmado digitalmente y CSV estandarizado para la Inspección de Trabajo.",
                        fontSize = 13.sp,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
            },
            confirmButton = {
                StyledButtonPrimary(
                    text = "Descargar Informe",
                    onClick = { showExportSuccessDialog = false }
                )
            }
        )
    }
}
